import { FC, forwardRef, MouseEvent, useMemo } from 'react';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { buildDailyNavEffectiveRules, dailyNavRowHasOverrides } from 'common/constants/dailyNav';
import { DAILY_NAV_RULES_ICON, DAILY_NAV_TOOLTIP_SLOT_PROPS } from 'pages/Intelligence/DailyNav/dailyNav.constants';
import { CompanyRulesIndicatorProps } from './CompanyRulesIndicator.props';

type RulesLinkProps = { settingsUrl?: string; color: string; label: string; testId: string };

// forwardRef and the spread are BOTH load-bearing: Tooltip anchors by ref and binds by
// injecting props. A plain function component swallows both and silently loses the
// tooltip while the link keeps working.
const RulesLink = forwardRef<HTMLAnchorElement, RulesLinkProps>(
  ({ settingsUrl, color, label, testId, ...tooltipHandlers }, ref) => (
    <Link
      {...tooltipHandlers}
      ref={ref}
      component={settingsUrl ? RouterLink : 'span'}
      to={settingsUrl}
      // The sheet treats a cell click as a selection. After the spread so an injected
      // handler cannot clobber it.
      onClick={(event: MouseEvent) => event.stopPropagation()}
      aria-label={settingsUrl ? `${label} — open this company's Daily NAV settings` : label}
      data-testid={testId}
      sx={{ display: 'inline-flex', color, cursor: settingsUrl ? 'pointer' : 'help' }}>
      <DAILY_NAV_RULES_ICON fontSize="small" />
    </Link>
  )
);
RulesLink.displayName = 'RulesLink';

// The rules this row is judged by, on hover. On EVERY row, not just overriding ones --
// someone asking why a cell went red needs the number either way. Colour, not presence,
// flags a company that departs from the firm template.
const CompanyRulesIndicator: FC<CompanyRulesIndicatorProps> = ({ config, settingsUrl }) => {
  const rules = useMemo(() => buildDailyNavEffectiveRules(config), [config]);
  const hasOverrides = dailyNavRowHasOverrides(config);

  if (!config) {
    return (
      <Tooltip
        describeChild
        arrow
        placement="right"
        slotProps={DAILY_NAV_TOOLTIP_SLOT_PROPS}
        title="This row is not anchored to any config version, so the rules it was evaluated under can no longer be identified.">
        <RulesLink
          settingsUrl={settingsUrl}
          color="error.main"
          label="Rules unknown"
          testId="daily-nav-row-rules-missing"
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip
      describeChild
      arrow
      placement="right"
      slotProps={DAILY_NAV_TOOLTIP_SLOT_PROPS}
      title={
        <Box>
          <Typography variant="caption" fontWeight="bold" display="block">
            {hasOverrides ? 'Company overrides some firm defaults' : 'Inherits the firm defaults'}
          </Typography>
          {rules.map((rule, index) => (
            <Box key={rule.key} sx={{ mt: index === 0 || rule.group !== rules[index - 1].group ? 0.75 : 0 }}>
              {(index === 0 || rule.group !== rules[index - 1].group) && (
                <Typography variant="caption" display="block" fontWeight="bold" sx={{ opacity: 0.7 }}>
                  {rule.group.toUpperCase()}
                </Typography>
              )}
              <Typography variant="caption" display="block">
                {rule.label}: <strong>{rule.value}</strong>
                {/* Only an override needs the firm value; inherited, they are equal. */}
                {rule.isOverride ? ` (firm default ${rule.firmValue})` : ''}
              </Typography>
            </Box>
          ))}
          {settingsUrl && (
            <Typography variant="caption" display="block" sx={{ mt: 0.75, opacity: 0.7 }}>
              Click to open this company&apos;s Daily NAV settings.
            </Typography>
          )}
        </Box>
      }>
      <RulesLink
        settingsUrl={settingsUrl}
        // Muted when inherited, so only a company with its own rules draws the eye.
        color={hasOverrides ? 'info.main' : 'action.disabled'}
        label={hasOverrides ? 'Company overrides some firm defaults' : 'Inherits the firm defaults'}
        testId={hasOverrides ? 'daily-nav-row-rules-custom' : 'daily-nav-row-rules-inherited'}
      />
    </Tooltip>
  );
};

export default CompanyRulesIndicator;
